import { defineComponent as _defineComponent } from 'vue'
import ChangeStoreSideMenuContents from 'JS/components/side-menus/change-store/ChangeStoreSideMenuContents.vue';
import InstallationQuote from './InstallationQuote.vue';
import InstallationQuoteBatteryProduct from './InstallationQuoteBatteryProduct.vue';
import InstallationQuoteError from './InstallationQuoteError.vue';
import InstallationQuoteOptionInfo from './InstallationQuoteOptionInfo.vue';
import InstallationQuoteTireProduct from './InstallationQuoteTireProduct.vue';
import LoginSideMenuContents from 'JS/components/side-menus/account-creation/components/LoginSideMenuContents.vue';
import RewardsSideMenuContents from 'JS/components/side-menus/rewards-sign-up/components/RewardsSideMenuContents.vue';
import SideMenu from 'Src/js/components/SideMenu.vue';
import SideMenuHeaderButton from 'JS/components/side-menus/SideMenuHeaderButton.vue';
import Spinner from 'JS/components/Spinner.vue';
import { computed, ref, watch } from 'vue';
import { CreateOauthAccountRequest, ICreateAccountResource, ICreateAccountResponse, LoginPages, LoginPageType } from 'JS/types/Account';
import { debounce } from 'JS/utilities/debounce';
import { delay } from 'JS/utilities/promise';
import { getMaxQuantityExceeded } from 'JS/helpers/TireQuantityHelper';
import { IAutomotiveWorkOrderLineItemInfo, IAutomotiveWorkOrderQuoteSummary } from 'JS/types/automotive/WorkOrderQuote';
import { IJoinRewardsStep, IVerifyUserData } from 'JS/types/rewards';
import { InstallationQuoteService } from 'JS/services/automotive/InstallationQuoteService';
import { IProductDetailProductJson } from 'JS/types/PDP';
import { PDPRootState } from 'JS/pdp/state/types';
import { PropType } from 'vue';
import { provideServices } from 'JS/components/side-menus/composables/services';
import { useDocumentVisibilityChange } from 'JS/composables/document-visibility';
import { useLegacyVuexStore } from 'JS/composables/legacy-vuex-store';


export default /*#__PURE__*/_defineComponent({
  __name: 'InstallationQuoteSidebar',
  props: {
	product: { type: Object as PropType<IProductDetailProductJson>, required: true },
	show: Boolean,
},
  emits: ["close"],
  setup(__props, { emit }: { emit: ({
	(e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props

const installationQuoteService = new InstallationQuoteService(); // TODO: inject




const store = useLegacyVuexStore<PDPRootState>();
const quantity = computed({
	get: () => {
		return store.state.quantity;
	},
	set: (val) => {
		store.commit('quantity', val);
	},
});

provideServices();

const addOns = ref(new Set<string>());
const quote = ref<IAutomotiveWorkOrderQuoteSummary | null>(null);
const error = ref<string | null>(null);
const isLoading = ref(false);
const loadingComplete = ref(false);
const showNestedSideMenu = ref<'change-store' | 'rewards' | 'login' | null>(null);
const backArrowRouter = ref<Array<IJoinRewardsStep | LoginPageType | 'quote'>>([]);
const joinRewardsStep = ref<IJoinRewardsStep>();
const currLoginPage = ref<LoginPageType>();
const forceReload = ref<boolean>(false);
const cameFromSignIn = ref<boolean>(false);

//For account creation -> rewards linking
const userVerificationData = ref<IVerifyUserData>(null);
const createAccountRequest = ref<ICreateAccountResource>(null);
const createAccountResponse = ref<ICreateAccountResponse>(null);
const createOauthAccountRequest = ref<CreateOauthAccountRequest>(null);

const showBackArrow = computed((): boolean => {
	return backArrowRouter.value.length != 0;
});

function determineBackArrow(prevPage: IJoinRewardsStep | LoginPageType | "clear") {
	if (prevPage === "clear") {
		if (cameFromSignIn.value) { //true when on rewards sign up after having logged in on the login sidemenu
			backArrowRouter.value = [];
		} else {
			backArrowRouter.value = ['quote'];
		}
	} else {
		backArrowRouter.value.push(prevPage);
	}
}

function handleBackArrow() {
	const page = backArrowRouter.value.pop();
	if (page === 'quote') {
		backArrowRouter.value = [];
		showNestedSideMenu.value = null;
		selectedItemInfo.value = null;
	} else if (isLoginPage(page)) {
		currLoginPage.value = page as LoginPageType;
		joinRewardsStep.value = null;
		showNestedSideMenu.value = 'login';
	} else {
		joinRewardsStep.value = page as IJoinRewardsStep;
		currLoginPage.value = null;
		showNestedSideMenu.value = 'rewards';
	}
}

function isLoginPage(value: string): boolean {
	return LoginPages.hasOwnProperty(value);
}

function changeSideMenu(sideMenu: "rewards" | "login" | "change-store") {
	backArrowRouter.value.push('quote');
	showNestedSideMenu.value = sideMenu;

	if (sideMenu === 'rewards') {
		joinRewardsStep.value = 'join';

	} else if (sideMenu === 'login') {
		currLoginPage.value = 'login';
	}
}

function handleRewardsClose() {
	if (forceReload.value) {
		window.location.reload();
		emit('close');
	} else {
		backArrowRouter.value = [];
		showNestedSideMenu.value = null;
	}
}

function reset() {
	selectedItemInfo.value = null;
	quote.value = null;
	showNestedSideMenu.value = null;
	backArrowRouter.value = [];
}

function determineClose() {
	if (forceReload.value) window.location.reload();
	emit('close');
}

function handleForceReloadEmit() {
	forceReload.value = true;
	backArrowRouter.value = [];
}

function handleSignedIn() {
	//No back button from rewards; if we show one and user goes back to FARMS then chooses
	//'join rewards again', they will be redirected to sign in again (despite already being logged in)
	backArrowRouter.value = [];
	userSignInHelper('join');
}

function handleGoToRewards(page: IJoinRewardsStep = "join", verificationData: IVerifyUserData = null, createData: ICreateAccountResource = null, returnPage: LoginPageType = null) {
	createOauthAccountRequest.value = null;

	if (page === "verification-join") {
		determineBackArrow(returnPage);
	} else if (page === "success") {
		determineBackArrow("clear");
	}

	userVerificationData.value = verificationData;
	createAccountRequest.value = createData;
	userSignInHelper(page);
}

function handleGoToRewardsWithOauth(page: IJoinRewardsStep = "join", verificationData: IVerifyUserData = null, createOauth: CreateOauthAccountRequest = null, returnPage: LoginPageType = null) {
	createAccountRequest.value = null;

	if (page === "verification-join") {
		determineBackArrow(returnPage);
	} else if (page === "success") {
		determineBackArrow("clear");
	}
	userVerificationData.value = verificationData;
	createOauthAccountRequest.value = createOauth;
	userSignInHelper(page);
}

function userSignInHelper(newStep: IJoinRewardsStep) {
	cameFromSignIn.value = true;
	forceReload.value = true;
	joinRewardsStep.value = newStep;
	showNestedSideMenu.value = 'rewards';
}

// used to ensure we only ever show results for the latest request
let lastRequest: Promise<IAutomotiveWorkOrderQuoteSummary>;
function getQuote(qty: number, addOnItemsBlainNumbers: string[]) {
	error.value = null;
	isLoading.value = true;
	loadingComplete.value = false;
	const req = delay(installationQuoteService.getQuote({
		blainNumber: props.product.blainNumber,
		qty,
		addOnItemsBlainNumbers,
		overrideSelectedAddOnsWithDefault: !quote.value,
	}), 750);
	lastRequest = req;

	req.then(q => {
		if (req !== lastRequest) {
			return;
		}

		quote.value = q;
		addOns.value = new Set(q.selectedAddOnLineItemBlainNumbers);
	}).catch(_e => {
		if (req !== lastRequest) {
			return;
		}

		error.value = "Something went wrong";
	}).finally(() => {
		if (req !== lastRequest) {
			return;
		}

		isLoading.value = false;
		loadingComplete.value = true;
	});
}

// undebounced watcher to update the quote when the sidebar opens
watch(() => props.show, show => {
	if (show) {
		getQuote(quantity.value, [...addOns.value]);
	}
}, { immediate: true }); // immediate here ensures that the quote is refetched when reloaded in dev mode

// debounced watcher for quantity changes
const getQuoteDebounced = debounce(250, getQuote);
watch(quantity, qty => {
	if (props.show) {
		getQuoteDebounced(qty, [...addOns.value]);
	}
});

useDocumentVisibilityChange(visible => {
	if (visible && props.show) {
		getQuote(quantity.value, [...addOns.value]);
	}
});

const selectedItemInfo = ref<IAutomotiveWorkOrderLineItemInfo | null>(null);
const titleEl = ref<HTMLElement | null>(null);
// focus the title when opening or closing item info
watch(selectedItemInfo, (newVal) => {
	if (newVal) {
		backArrowRouter.value.push('quote');
	}
	titleEl.value?.focus();
});

const maxQuantityExceeded = computed(() => getMaxQuantityExceeded(props.product, quantity.value));

const minBuyableQty = computed(() => store.getters.minBuyableQty);
const price = computed(() => quote.value?.productPrice ?? props.product.price);

async function checkAddOnItem(blainNumber: string) {
	if (addOns.value.has(blainNumber)) {
		addOns.value.delete(blainNumber);
	} else {
		addOns.value.add(blainNumber);
	}

	addOns.value = new Set(addOns.value); // reassignment because Vue 2 can't make Sets reactive
	getQuote(quantity.value, [...addOns.value]);
}


return { __sfc: true,installationQuoteService, props, emit, store, quantity, addOns, quote, error, isLoading, loadingComplete, showNestedSideMenu, backArrowRouter, joinRewardsStep, currLoginPage, forceReload, cameFromSignIn, userVerificationData, createAccountRequest, createAccountResponse, createOauthAccountRequest, showBackArrow, determineBackArrow, handleBackArrow, isLoginPage, changeSideMenu, handleRewardsClose, reset, determineClose, handleForceReloadEmit, handleSignedIn, handleGoToRewards, handleGoToRewardsWithOauth, userSignInHelper, lastRequest, getQuote, getQuoteDebounced, selectedItemInfo, titleEl, maxQuantityExceeded, minBuyableQty, price, checkAddOnItem, ChangeStoreSideMenuContents, InstallationQuote, InstallationQuoteBatteryProduct, InstallationQuoteError, InstallationQuoteOptionInfo, InstallationQuoteTireProduct, LoginSideMenuContents, RewardsSideMenuContents, SideMenu, SideMenuHeaderButton, Spinner }
}

})